import React, { useState } from "react";
import {
  ListValue,
  useUpdateCustomerFieldMutation,
} from "../../api/customerFieldApi";
import { Button } from "../../components/tailwind/button/Button";

interface InternalListValue extends ListValue {
  _id: string;
}

interface Props {
  fieldId: number;
  listValues: ListValue[] | null;
}

let idCounter = 0;
const generateId = (): string => `list-value-${++idCounter}`;

const toInternalValues = (values: ListValue[] | null): InternalListValue[] => {
  return (values || []).map((v) => ({ ...v, _id: generateId() }));
};

const toExternalValues = (values: InternalListValue[]): ListValue[] => {
  return values.map(({ value, label }) => ({ value, label }));
};

const isBlank = (input: string | null | undefined): boolean =>
  !(input ?? "").trim();

export const ListValuesEditor: React.FC<Props> = ({ fieldId, listValues }) => {
  const [
    updateField,
    { isLoading: isSaving },
  ] = useUpdateCustomerFieldMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<InternalListValue[]>([]);

  const handleStartEdit = (): void => {
    setEditValues(toInternalValues(listValues));
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setIsEditing(false);
    setEditValues([]);
  };

  const handleSave = async (): Promise<void> => {
    const external = toExternalValues(editValues);
    await updateField({
      id: fieldId,
      list_values: external.length > 0 ? external : null,
    });
    setIsEditing(false);
    setEditValues([]);
  };

  const handleValueChange = (
    id: string,
    field: "value" | "label",
    newValue: string
  ): void => {
    setEditValues((prev) =>
      prev.map((v) => (v._id === id ? { ...v, [field]: newValue } : v))
    );
  };

  const handleAddValue = (): void => {
    setEditValues((prev) => [
      ...prev,
      { value: "", label: "", _id: generateId() },
    ]);
  };

  const handleDeleteValue = (id: string): void => {
    setEditValues((prev) => prev.filter((v) => v._id !== id));
  };

  const hasIncompleteValues = editValues.some(
    (v) => isBlank(v.value) || isBlank(v.label)
  );

  const displayValues = listValues || [];

  // Read-only view
  if (!isEditing) {
    return (
      <div className="tw-p-4">
        {displayValues.length === 0 ? (
          <div className="tw-text-sm tw-text-gray-500 tw-mb-4">
            No list values defined. Add values that can be selected for this
            field.
          </div>
        ) : (
          <div className="tw-space-y-1 tw-mb-4">
            {displayValues.map((item) => (
              <div
                key={item.value}
                className="tw-flex tw-gap-3 tw-text-sm tw-py-1.5 tw-px-2 tw-bg-white tw-rounded tw-border tw-border-gray-200"
              >
                <span className="tw-font-mono tw-text-gray-600">
                  {item.value}
                </span>
                <span className="tw-text-gray-400">→</span>
                <span className="tw-text-gray-800">{item.label}</span>
              </div>
            ))}
          </div>
        )}

        <Button variant="secondary" size="sm" onClick={handleStartEdit}>
          <i className="material-icons tw-text-sm tw-mr-1">edit</i>
          Edit list values
        </Button>
      </div>
    );
  }

  // Edit view
  return (
    <div className="tw-p-4">
      <div className="tw-space-y-2 tw-mb-4">
        {/* Header */}
        {editValues.length > 0 && (
          <div className="tw-flex tw-gap-3 tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-px-1">
            <div className="tw-flex-1">Value</div>
            <div className="tw-flex-1">Label</div>
            <div className="tw-w-8"></div>
          </div>
        )}

        {/* Rows */}
        {editValues.map((item) => (
          <div key={item._id} className="tw-flex tw-items-center tw-gap-3">
            <input
              type="text"
              value={item.value ?? ""}
              onChange={(e) =>
                handleValueChange(item._id, "value", e.target.value)
              }
              placeholder="e.g. material_cotton"
              className={`tw-flex-1 tw-rounded-md tw-shadow-sm tw-text-sm tw-py-1.5 tw-px-2 ${
                isBlank(item.value)
                  ? "tw-border-red-300 tw-ring-1 tw-ring-red-300 focus:tw-border-red-500 focus:tw-ring-red-500"
                  : "tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500"
              }`}
            />
            <input
              type="text"
              value={item.label ?? ""}
              onChange={(e) =>
                handleValueChange(item._id, "label", e.target.value)
              }
              placeholder="e.g. Cotton"
              className={`tw-flex-1 tw-rounded-md tw-shadow-sm tw-text-sm tw-py-1.5 tw-px-2 ${
                isBlank(item.label)
                  ? "tw-border-red-300 tw-ring-1 tw-ring-red-300 focus:tw-border-red-500 focus:tw-ring-red-500"
                  : "tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500"
              }`}
            />
            <button
              onClick={() => handleDeleteValue(item._id)}
              className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
              title="Delete value"
            >
              <i className="material-icons tw-text-lg">close</i>
            </button>
          </div>
        ))}

        {editValues.length === 0 && (
          <div className="tw-text-sm tw-text-gray-500">
            No values yet. Click &quot;Add value&quot; to add one.
          </div>
        )}
      </div>

      <div className="tw-flex tw-gap-2">
        <Button variant="secondary" size="sm" onClick={handleAddValue}>
          <i className="material-icons tw-text-sm tw-mr-1">add</i>
          Add value
        </Button>

        <div className="tw-flex-1"></div>

        <Button variant="secondary" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isSaving || hasIncompleteValues}
          title={
            hasIncompleteValues
              ? "Fill in all values and labels before saving"
              : undefined
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>

      {hasIncompleteValues && (
        <div className="tw-text-xs tw-text-red-500 tw-mt-2">
          All values and labels must be filled in before saving.
        </div>
      )}
    </div>
  );
};
